// Portal page: /portal/invoice?token=<pay_token>
//
// This is the page the client lands on from the invoice email. It was
// referenced by the send-invoice flow for months but never existed — clients
// clicked "Pay" and got the portal login. Public: the emailed token IS the
// auth. Three states:
//   • hosted checkout processor (Fanbasis) → one button to the hosted page
//   • direct charge (Authorize.net) → Accept.js card form; the card is
//     tokenized in the browser and never touches our servers
//   • paid / void / expired → clear message, no form
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PortalShell } from "../components/PortalShell";
import { ApiError, portal, type PortalInvoiceView } from "../lib/api";

declare global {
  interface Window {
    Accept?: {
      dispatchData: (
        data: { authData: { clientKey: string; apiLoginID: string }; cardData: { cardNumber: string; month: string; year: string; cardCode: string; zip?: string } },
        cb: (r: { messages: { resultCode: string; message: Array<{ code: string; text: string }> }; opaqueData?: { dataDescriptor: string; dataValue: string } }) => void,
      ) => void;
    };
  }
}

const BRAND = "No Excuses Disputes";

function money(cents: number, currency: string) {
  const amt = (cents || 0) / 100;
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "USD").toUpperCase() }).format(amt);
  } catch {
    return `$${amt.toFixed(2)}`; // an unexpected stored currency code must not blank the page
  }
}

/** Load Accept.js for the right environment exactly once. */
function useAcceptJs(mode: string | undefined, enabled: boolean) {
  const [ready, setReady] = useState<boolean>(() => !!window.Accept);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    if (window.Accept) { setReady(true); return; }
    const src = mode === "production" ? "https://js.authorize.net/v1/Accept.js" : "https://jstest.authorize.net/v1/Accept.js";
    const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;
    const el = existing || document.createElement("script");
    const onLoad = () => setReady(!!window.Accept);
    const onErr = () => setFailed(true);
    el.addEventListener("load", onLoad); el.addEventListener("error", onErr);
    if (!existing) { el.src = src; el.async = true; el.setAttribute("charset", "utf-8"); document.body.appendChild(el); }
    else if (window.Accept) setReady(true);
    return () => { el.removeEventListener("load", onLoad); el.removeEventListener("error", onErr); };
  }, [mode, enabled]);
  return { ready, failed };
}

export default function PortalInvoicePayPage() {
  const [search] = useSearchParams();
  const token = (search.get("token") || "").trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<PortalInvoiceView | null>(null);

  const [card, setCard] = useState({ number: "", month: "", year: "", cvv: "", zip: "" });
  const [paying, setPaying] = useState(false);
  const [payErr, setPayErr] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<{ processor_charge_id: string; amount_cents: number; currency: string; paid_at: string } | null>(null);

  useEffect(() => {
    if (!token) { setError("This payment link is missing its token. Please use the link from your invoice email."); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const v = await portal.getInvoice(token);
        if (!cancelled) setView(v);
      } catch (e: any) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) setError("We couldn't find this invoice. The link may be old — please contact us for a fresh one.");
        else if (e instanceof ApiError && e.status === 410) setError("This payment link has expired. Please contact us and we'll send a new one.");
        else setError(e?.body?.message || e?.message || "Could not load the invoice.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const isAuthNet = view?.processor?.id === "authorize_net" && !!view?.acceptjs;
  const { ready: acceptReady, failed: acceptFailed } = useAcceptJs(view?.acceptjs?.mode, isAuthNet && view?.invoice.status !== "paid");

  const lineItems = useMemo(() => (Array.isArray(view?.invoice.line_items) ? view!.invoice.line_items : []), [view]);

  const onPay = useCallback(() => {
    setPayErr(null);
    if (!view?.acceptjs || !window.Accept) { setPayErr("The secure card form has not finished loading. Please wait a moment and try again."); return; }
    const num = card.number.replace(/\s+/g, "");
    if (!/^\d{13,19}$/.test(num)) return setPayErr("Enter a valid card number.");
    if (!/^(0[1-9]|1[0-2])$/.test(card.month)) return setPayErr("Enter the expiration month as two digits (01–12).");
    if (!/^\d{2}(\d{2})?$/.test(card.year)) return setPayErr("Enter the expiration year (YY or YYYY).");
    if (!/^\d{3,4}$/.test(card.cvv)) return setPayErr("Enter the 3- or 4-digit security code.");
    setPaying(true);
    const year = card.year.length === 2 ? `20${card.year}` : card.year;
    window.Accept.dispatchData(
      {
        authData: { clientKey: view.acceptjs.client_key, apiLoginID: view.acceptjs.api_login_id },
        cardData: { cardNumber: num, month: card.month, year, cardCode: card.cvv, zip: card.zip || undefined },
      },
      async (r) => {
        if (r.messages.resultCode !== "Ok" || !r.opaqueData) {
          setPaying(false);
          setPayErr(r.messages.message?.map((m) => m.text).join(" ") || "The card could not be verified. Check the details and try again.");
          return;
        }
        try {
          const res = await portal.chargeInvoice(token, r.opaqueData);
          setReceipt({ processor_charge_id: res.processor_charge_id, amount_cents: res.amount_cents, currency: res.currency, paid_at: res.paid_at });
          setCard({ number: "", month: "", year: "", cvv: "", zip: "" });
        } catch (e: any) {
          const msg = (e instanceof ApiError && (e.body?.message || e.body?.error)) || e?.message || "The payment did not go through.";
          setPayErr(msg);
        } finally { setPaying(false); }
      },
    );
  }, [view, card, token]);

  if (loading) {
    return (<PortalShell><div className="portal-card" role="status" aria-live="polite"><p className="portal-sub on-card">Loading your invoice…</p></div></PortalShell>);
  }
  if (error || !view) {
    return (<PortalShell><div className="portal-card" role="alert"><h1 className="portal-headline on-card" style={{ fontSize: 24 }}>This payment link can't be used.</h1><p className="portal-sub on-card">{error || "Invoice not found."}</p></div></PortalShell>);
  }

  const inv = view.invoice;
  const paid = inv.status === "paid" || !!receipt || !!view.payment_received;
  const closed = inv.status === "void" || inv.status === "refunded";
  const total = money(inv.total_cents, inv.currency);

  return (
    <PortalShell>
      <section className="portal-section">
        <p className="portal-eyebrow">Invoice from {view.company?.name || BRAND}</p>
        <h1 className="portal-headline">{inv.client_name ? `${inv.client_name}, ` : ""}your invoice for {total}</h1>
        {inv.description && <p className="portal-sub">{inv.description}</p>}
      </section>

      <div className="portal-card" data-testid="invoice-summary" role="region" aria-label="Invoice details">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 15 }}>
          <tbody>
            {lineItems.map((li, i) => (
              <tr key={i} style={{ borderBottom: "1px solid var(--ne-border, #e5e7eb)" }}>
                <td style={{ padding: "8px 0" }}>{li.description}</td>
                <td style={{ padding: "8px 0", textAlign: "right", whiteSpace: "nowrap" }}>{money(li.amount_cents, inv.currency)}</td>
              </tr>
            ))}
            <tr>
              <td style={{ padding: "10px 0", fontWeight: 700 }}>Total due</td>
              <td style={{ padding: "10px 0", textAlign: "right", fontWeight: 700 }}>{total}</td>
            </tr>
          </tbody>
        </table>
        <p className="portal-sub on-card" style={{ marginTop: 8, fontSize: 13 }}>
          Under the Credit Repair Organizations Act, this invoice covers work that has already been completed for you.
        </p>
      </div>

      {paid && (
        <div className="portal-card" role="status" aria-live="polite" data-testid="invoice-paid"
             style={{ background: "var(--ne-success-bg)", borderColor: "var(--ne-success-border)" }}>
          <h2 className="portal-headline on-card" style={{ fontSize: 22, color: "var(--ne-success-text)" }}>
            {receipt ? "Payment received — thank you." : "This invoice is paid."}
          </h2>
          <p className="portal-sub on-card">
            {receipt
              ? `${money(receipt.amount_cents, receipt.currency)} was charged to your card. Confirmation number ${receipt.processor_charge_id}. A receipt has been recorded on your account.`
              : "Nothing further is due on this invoice."}
          </p>
        </div>
      )}

      {!paid && closed && (
        <div className="portal-card" role="alert"><h2 className="portal-headline on-card" style={{ fontSize: 22 }}>This invoice is no longer payable.</h2><p className="portal-sub on-card">Please contact us if you have questions.</p></div>
      )}

      {!paid && !closed && view.processor?.supports_hosted_checkout && view.processor.hosted_checkout_url && (
        <div className="portal-card">
          <h2 className="portal-headline on-card" style={{ fontSize: 22 }}>Pay securely</h2>
          <p className="portal-sub on-card">You'll be taken to our payment partner to complete the payment.</p>
          <a className="primary-button" href={view.processor.hosted_checkout_url} rel="noopener">Pay {total}</a>
        </div>
      )}

      {!paid && !closed && view.processor?.id === "authorize_net" && !view.acceptjs && (
        <div className="portal-card" role="alert" data-testid="invoice-setup-problem">
          <h2 className="portal-headline on-card" style={{ fontSize: 22 }}>Online payment isn't available for this invoice yet.</h2>
          <p className="portal-sub on-card">{view.setup_problem || "Please contact us to arrange payment."}</p>
        </div>
      )}

      {!paid && !closed && isAuthNet && (
        <div className="portal-card" data-testid="card-form">
          <h2 className="portal-headline on-card" style={{ fontSize: 22 }}>Pay {total} by card</h2>
          <p className="portal-sub on-card" style={{ fontSize: 13 }}>
            Your card details are encrypted in your browser and sent directly to Authorize.net. {view.company?.name || BRAND} never sees your card number.
          </p>
          {acceptFailed && <p className="error-text">The secure payment script could not load. Check your connection or try another browser.</p>}
          <form onSubmit={(e) => { e.preventDefault(); onPay(); }} style={{ display: "grid", gap: 12, marginTop: 8 }} autoComplete="on">
            <label className="field-label">Card number
              <input inputMode="numeric" autoComplete="cc-number" placeholder="1234 5678 9012 3456" value={card.number}
                     onChange={(e) => setCard({ ...card, number: e.target.value })} required aria-label="Card number" />
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <label className="field-label">Exp. month
                <input inputMode="numeric" autoComplete="cc-exp-month" placeholder="MM" maxLength={2} value={card.month}
                       onChange={(e) => setCard({ ...card, month: e.target.value.replace(/\D/g, "") })} required aria-label="Expiration month" />
              </label>
              <label className="field-label">Exp. year
                <input inputMode="numeric" autoComplete="cc-exp-year" placeholder="YYYY" maxLength={4} value={card.year}
                       onChange={(e) => setCard({ ...card, year: e.target.value.replace(/\D/g, "") })} required aria-label="Expiration year" />
              </label>
              <label className="field-label">Security code
                <input inputMode="numeric" autoComplete="cc-csc" placeholder="CVV" maxLength={4} value={card.cvv}
                       onChange={(e) => setCard({ ...card, cvv: e.target.value.replace(/\D/g, "") })} required aria-label="Security code" />
              </label>
            </div>
            <label className="field-label">Billing ZIP
              <input inputMode="numeric" autoComplete="postal-code" placeholder="ZIP" maxLength={10} value={card.zip}
                     onChange={(e) => setCard({ ...card, zip: e.target.value })} aria-label="Billing ZIP code" />
            </label>
            {payErr && <p className="error-text" role="alert">{payErr}</p>}
            <button type="submit" className="primary-button" disabled={paying || !acceptReady} aria-busy={paying}>
              {paying ? "Processing…" : acceptReady ? `Pay ${total}` : "Loading secure form…"}
            </button>
          </form>
        </div>
      )}
    </PortalShell>
  );
}
